import React, { useEffect, useRef } from 'react';

import type { SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { atoms, colors, Primitive, TextField } from '@rtkwlf/fenrir-react';
import { faSearch } from '@rtkwlf/pro-solid-svg-icons/faSearch';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';
import type {
  ColumnInstance,
  Row,
  TableState,
  TableToggleRowsSelectedProps,
} from 'react-table';
import {
  useExpanded,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { useDebouncedCallback } from 'use-debounce';

import { NoData } from '@packages/components/widgets/NoData';
import { usePrevious } from '@packages/hooks/usePrevious';
import { isNotEmpty } from '@packages/utils/base.utils';
import { DEBOUNCE_TIME_MEDIUM } from 'ae-constants/constants';

import { Pagination } from './Pagination';
import { TABLE_KEYS } from './table.constants';
import type { SortColumnType, SortFn, TableCell } from './table.types';
import { TableHeader } from './TableHeader';

export * from './table.types';
export * from './table.utils';

const tableClassName = atoms({ width: 'full' });
const cellClassName = atoms({
  borderBottomWidth: 'default',
  borderColor: 'neutralDefault',
  borderStyle: 'solid',
  borderWidth: 'zero',
  margin: 'zero',
  paddingBottom: 'small',
  paddingLeft: 'default',
  paddingRight: 'default',
  paddingTop: 'small',
  textAlign: 'left',
  textColor: 'body',
  wordBreak: 'break-word',
});

export type TableDataType = Record<string, unknown>[];

interface ExpansionRow extends Row<Record<string, unknown>> {
  isExpanded: boolean;
}

export interface RowExpandProps {
  row: ExpansionRow;
}

export type UpdateDataFnType = (
  rowIndex: number,
  columnId: string,
  value: any,
) => void;
export interface TableProps {
  /** Style returned by EmotionJS css function */
  style?: SerializedStyles;

  /** Array of ColumnInstance, it should be memoized as per the react-table requirements. */
  columns: Array<ColumnInstance>;

  /** Array of objects, this will be rendered as table rows. */
  data: TableDataType;

  /**
   * Callback function for handling sort.
   * It returns the column name and the sort state i.e. none, asc, or desc.
   */
  onSort?: SortFn;

  /** This is required to enable server side pagination. Default is true */
  manualPagination?: boolean;

  /**
   * This is required for accessibility purposes for pagination,
   * see more details in Pagination Component.
   */
  paginationLabel?: string;

  /** To show or hide pagination UI in the Table. Default is true */
  showPagination?: boolean;

  /**
   * Defines the number of rows shown in one page for the purpose of pagination.
   * Default is 10
   */
  rowsPerPage?: number;

  /**
   * Total number of rows available for a data set on the server, required for
   * server side pagination.
   */
  totalRows: number;

  /**
   *  Callback for pagination.
   */
  onPaginate?: (pageIndex: number, pageSize: number) => void;

  /**
   * Callback function for handling row clicks.
   */
  onRowClick?: (rowObject: Record<string, unknown>, index: number) => void;

  /**
   * Must be *memoized*.
   * An array of sorting objects. If there is more than one object in the array,
   * multi-sorting will be enabled. Each sorting object should contain an id key
   * with the corresponding column ID to sort by. An optional desc key (which defaults to false)
   * may be set to true to indicate a descending sorting direction for that column, otherwise,
   * it will be assumed to be ascending.
   */
  initialSortBy?: SortColumnType;

  /** This is required to enable server side sorting. Default is true */
  manualSortBy?: boolean;

  /** Disables multi-sorting for client side sorting. Default is false (enabled) */
  disableMultiSort?: boolean;

  /** To show or hide bulk row selection checkbox column. Default is false */
  showBulkRowSelection?: boolean;

  /** To show or hide bulk rows selection checkbox column. Default is true */
  showBulkRowsSelection?: boolean;

  /**
   * Callback function for handling rows selections.
   */
  onRowSelect?: (selectedRows: Record<string, unknown>[]) => void;

  /**
   * Current Page
   */
  currentPage?: number;

  expandedRowComponent?: React.FC<RowExpandProps>;

  /**
   * Allows us to set sort by option
   * Using this props we can apply dynamic sorting
   */
  updatedSortBy?: SortColumnType;

  /**
   * Allows us to deselect all selected rows
   */
  deselectRows?: boolean;

  /** This is required to enable global filtering for all columns data. Default is false */
  enableGlobalFilter?: boolean;

  /** Wrapper style returned by EmotionJS css function for table wrapper */
  wrapperStyle?: SerializedStyles;

  /** Array of indexes to set initial row selections */
  initialSelected?: Array<string>;

  /** The data for the row that should currently be active. Default is null (no active row). */
  activeRowData?: unknown | null;

  /** Custom util to enable edit data from within the table  */
  updateData?: UpdateDataFnType;
}

// TODO: Careful typings is super bad!
interface CellType {
  row: {
    toggleRowSelected: (set?: boolean) => void;
    getToggleRowSelectedProps: (
      props?: Partial<TableToggleRowsSelectedProps>,
    ) => TableToggleRowsSelectedProps;
    original: Record<string, any>;
  };
}

interface IndeterminateCheckboxProps {
  indeterminate?: boolean;
}

const ROW_SELECTION_ID = 'ROW_SELECTION_ID';

const IndeterminateCheckbox = React.forwardRef<
  HTMLInputElement,
  IndeterminateCheckboxProps
>(({ indeterminate, ...rest }, ref) => {
  const defaultRef = React.useRef(null);
  const resolvedRef = ref || defaultRef;

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  return (
    <input
      type="checkbox"
      ref={resolvedRef}
      // stopPropagation is needed to prevent onRowClick call after selecting a checkbox
      onClick={(e) => e.stopPropagation()}
      {...rest}
    />
  );
});

/** @deprecated Use the Fenrir's table */
export const Table: React.FC<TableProps> = ({
  columns,
  data,
  style,
  totalRows,
  onSort,
  showPagination = true,
  onPaginate,
  rowsPerPage = 10,
  paginationLabel,
  onRowClick,
  initialSortBy,
  showBulkRowSelection = false,
  showBulkRowsSelection = true,
  onRowSelect,
  currentPage = 0,
  manualPagination = true,
  manualSortBy = true,
  disableMultiSort,
  expandedRowComponent: ExpandedRowComponent,
  updatedSortBy,
  deselectRows,
  enableGlobalFilter = false,
  wrapperStyle,
  initialSelected,
  activeRowData = null,
  updateData,
}) => {
  const { t } = useTranslation();

  const numberOfPage = Math.ceil(totalRows / rowsPerPage);
  const initialState: Partial<TableState<Record<string, unknown>>> = {
    pageIndex: currentPage,
  };

  if (initialSortBy) {
    initialState.sortBy = initialSortBy;
  }
  if (rowsPerPage) {
    initialState.pageSize = rowsPerPage;
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setSortBy,
    toggleAllRowsSelected,
    selectedFlatRows,
    visibleColumns,
    state: { pageIndex, pageSize, sortBy },
    globalFilter,
    setGlobalFilter,
    toggleRowSelected,
  } = useTable(
    {
      columns,
      data,
      disableMultiSort,

      enableGlobalFilter,

      initialState,

      // This function returns false to not support multi-sort feature.
      isMultiSortEvent: () => false,

      // if true, will enable server side pagination.
      manualPagination,
      // if true, will enable server side sorting.
      manualSortBy,
      pageCount: numberOfPage,
      updateData,
    },
    useGlobalFilter,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      if (showBulkRowSelection) {
        hooks.visibleColumns.push((tableColumns) => [
          {
            Cell: ({ row }: CellType) => {
              const props = { ...row.getToggleRowSelectedProps() };
              return <IndeterminateCheckbox {...props} />;
            },
            Header: ({ getToggleAllRowsSelectedProps }) =>
              showBulkRowsSelection ? (
                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
              ) : null,
            id: ROW_SELECTION_ID,
            width: '2',
          },
          ...tableColumns,
        ]);
      }
    },
  );

  const debouncedSetFilter = useDebouncedCallback(
    setGlobalFilter,
    DEBOUNCE_TIME_MEDIUM,
  );

  useEffect(() => {
    initialSelected?.forEach((i) => {
      toggleRowSelected(i, true);
    });
  }, [initialSelected, toggleRowSelected]);

  useEffect(() => {
    if (isNotEmpty(updatedSortBy)) {
      setSortBy(updatedSortBy as SortColumnType);
    }
  }, [updatedSortBy, setSortBy]);

  useEffect(() => {
    setPageSize(rowsPerPage);
  }, [rowsPerPage, setPageSize]);

  // initialLoad will avoid onRowSelect at render
  const initialLoad = useRef(true);
  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }

    const selectedRows: Record<string, unknown>[] = selectedFlatRows.map(
      (row) => row.original,
    );
    onRowSelect?.(selectedRows);
  }, [selectedFlatRows, onRowSelect]);

  // Listen for page changes and call the corresponding handler
  useEffect(() => {
    if (typeof onPaginate === 'function') {
      onPaginate(pageIndex, pageSize);
    }
  }, [onPaginate, pageIndex, pageSize]);

  const previousSortBy = usePrevious(sortBy);
  useEffect(() => {
    if (typeof onSort === 'function' && !isEqual(previousSortBy, sortBy)) {
      onSort(sortBy);
    }
  }, [onSort, sortBy, previousSortBy]);

  // When the filters are applied or changed the currentPage is reset to 0.
  // This should also reset the pagination and the Table internal state as well.
  useEffect(() => {
    if (currentPage === 0) {
      gotoPage(0);
    }
  }, [gotoPage, currentPage]);

  useEffect(() => {
    if (deselectRows) {
      toggleAllRowsSelected(false);
    }
  }, [deselectRows, toggleAllRowsSelected]);

  return (
    <Primitive.div
      css={css`
        ${wrapperStyle}
      `}
    >
      {enableGlobalFilter ? (
        <Primitive.div width="25%" marginBottom="default">
          <TextField
            iconRight={<FontAwesomeIcon icon={faSearch} />}
            placeholder={t('components:filter.placeholder.search')}
            value={globalFilter}
            onChange={(e) => {
              debouncedSetFilter(e.target.value);
            }}
          />
        </Primitive.div>
      ) : null}

      {/* Note: For global filter case, if filtered data is empty array then display No Data widget since it is a static filter by react-table.
          For dynamic filters case (non-global filter case), No Data widget case is handled in ContentWrapper component.
      */}
      {enableGlobalFilter && rows.length === 0 ? (
        <NoData />
      ) : (
        <Primitive.div
          css={css`
            overflow-x: auto;
            ${style}
          `}
        >
          <table
            {...getTableProps()}
            role="table"
            aria-label={t('table:table')}
            className={tableClassName}
            css={css`
              border-spacing: 0;
            `}
          >
            <TableHeader headerGroups={headerGroups} />
            <tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                const expansionRow = row as ExpansionRow;
                prepareRow(row);
                const trProps = row.getRowProps();
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      {...trProps}
                      key={trProps.key}
                      onClick={() => {
                        onRowClick?.(row.original, index);
                      }}
                      css={css`
                        :hover {
                          background-color: rgb(
                            ${expansionRow.isExpanded ||
                            row.original === activeRowData
                              ? colors.background.brand.highlight
                              : colors.background.widget.secondary}
                          );
                        }
                        background-color: rgb(
                          ${expansionRow.isExpanded ||
                          row.original === activeRowData
                            ? colors.background.info.highlight
                            : colors.background.widget.default}
                        );
                      `}
                    >
                      {row.cells.map((cell: TableCell) => {
                        const tdProps = cell.getCellProps();
                        return (
                          <td
                            {...tdProps}
                            key={tdProps.key}
                            className={cellClassName}
                            // This test id is required for test because we are using atom for sticky column styling.
                            data-testid={
                              cell.column.stickyStyle ? 'sticky-column' : ''
                            }
                            css={cell.column.stickyStyle ?? css``}
                          >
                            {cell.render(TABLE_KEYS.CELL) as React.ReactNode}
                          </td>
                        );
                      })}
                    </tr>
                    {expansionRow.isExpanded && ExpandedRowComponent && (
                      <tr>
                        {}
                        <td colSpan={visibleColumns.length}>
                          <ExpandedRowComponent row={expansionRow} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Primitive.div>
      )}

      {/* Note: For global filter case, if filtered data is empty array then do not display the pagination since it is a static filter by react-table. */}
      {showPagination &&
      enableGlobalFilter &&
      rows.length === 0 ? null : showPagination ? (
        <Pagination
          page={page}
          ariaLabel={paginationLabel}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          gotoPage={gotoPage}
          nextPage={nextPage}
          previousPage={previousPage}
          pageOptions={pageOptions}
          pageCount={pageCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPageSize={setPageSize}
          // Note: For global filter case, use rows array provided by react-table.
          totalRows={enableGlobalFilter ? rows.length : totalRows}
          // Note: For global filter case, use pageOptions array provided by react-table.
          totalPageCount={
            enableGlobalFilter ? pageOptions.length : numberOfPage
          }
        />
      ) : null}
    </Primitive.div>
  );
};
